import { Link, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import API, { BACKEND_URL } from "../api/api";
import SignaturePad from "../components/SignaturePad";
import {
  confirmAdminReservationCheckin,
  getAdminReservationCheckin
} from "../api/adminCheckins";

const imageBase = BACKEND_URL;
const formatDate = (value) => value ? new Date(value).toLocaleDateString("nl-BE") : "-";

export default function AdminCheckinStart() {
  const { reservationId } = useParams();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState({});
  const [optionChecks, setOptionChecks] = useState({});
  const [hasDamage, setHasDamage] = useState(false);
  const [damageDescription, setDamageDescription] = useState("");
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [signatureError, setSignatureError] = useState("");
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "checkinReservation", reservationId],
    queryFn: () => getAdminReservationCheckin(reservationId)
  });

  const trailerChecklist = useMemo(() => data?.requiredTrailerPhotoChecklist || [], [data]);
  const optionChecklist = useMemo(() => data?.requiredOptionChecklist || [], [data]);
  const missingTrailerPhotos = useMemo(() => (
    trailerChecklist.filter((item) => !photos[item.type]?.imageUrl)
  ), [trailerChecklist, photos]);
  const missingOptionChecks = useMemo(() => (
    optionChecklist.filter((item) => {
      const check = optionChecks[item.optionName];
      return !check?.photoUrl || !check?.status || (["DAMAGED", "MISSING"].includes(check.status) && !check.notes?.trim());
    })
  ), [optionChecklist, optionChecks]);
  const damageMissing = hasDamage && (!damageDescription.trim() || damagePhotos.length === 0);
  const damageDescriptionMissing = hasDamage && !damageDescription.trim();
  const damagePhotoMissing = hasDamage && damagePhotos.length === 0;

  const uploadMutation = useMutation({
    mutationFn: async ({ file }) => {
      const formData = new FormData();
      formData.append("image", file);
      const res = await API.post(`/upload/${reservationId}`, formData);
      return res.data.filePath;
    }
  });

  const uploadPhoto = async (file) => uploadMutation.mutateAsync({ file });

  const confirmMutation = useMutation({
    mutationFn: () => confirmAdminReservationCheckin(reservationId, {
      notes,
      photos: Object.values(photos),
      optionChecks: optionChecklist.map((item) => optionChecks[item.optionName]).filter(Boolean),
      hasDamage,
      damageDescription,
      damagePhotos,
      signatureDataUrl
    }),
    onSuccess: (result) => {
      setConfirmed(result);
      queryClient.invalidateQueries({ queryKey: ["admin", "checkins"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "checkinReservation", reservationId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "settlements"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["trailers"] });
      toast.success("Check-in bevestigd. Waarborg staat klaar voor afhandeling.");
    },
    onError: (error) => toast.error(error.response?.data?.error || "Check-in bevestigen mislukt")
  });

  const requiredChecksComplete = missingTrailerPhotos.length === 0 && missingOptionChecks.length === 0 && !damageMissing;
  const canSubmit = requiredChecksComplete && Boolean(signatureDataUrl);

  const submitCheckin = () => {
    if (!signatureDataUrl) {
      setSignatureError("Handtekening is verplicht.");
      toast.error("Plaats eerst een handtekening.");
      return;
    }
    confirmMutation.mutate();
  };

  if (isLoading) return <div className="loading-state">Check-in laden...</div>;

  if (isError || !data) {
    return (
      <section className="page">
        <div className="panel panel-pad">
          <h1 className="page-title">Check-in niet gevonden</h1>
          <Link className="button secondary" to="/admin/checkins">Terug naar check-ins</Link>
        </div>
      </section>
    );
  }

  if (data.checkInInspection) {
    return (
      <section className="page">
        <div className="panel panel-pad">
          <h1 className="page-title">Check-in bestaat al</h1>
          <p className="page-subtitle">Voor deze reservatie is al een check-in bevestigd.</p>
          <Link className="button secondary" to={`/admin/checkins/${data.checkInInspection.id}`}>Bekijk check-in</Link>
        </div>
      </section>
    );
  }

  if (confirmed) {
    return (
      <section className="page">
        <div className="panel panel-pad checkout-success">
          <h1 className="page-title">Check-in bevestigd</h1>
          <p className="page-subtitle">De trailer is terug ontvangen. Status: {confirmed.trailer.status}. Waarborg staat klaar voor afhandeling.</p>
          <div className="action-row">
            <Link className="button" to={`/admin/checkins/${confirmed.inspection.id}`}>Bekijk check-in</Link>
            {confirmed.settlement && <Link className="button secondary" to={`/admin/settlements/${confirmed.settlement.id}`}>Waarborg afhandelen</Link>}
            <Link className="button secondary" to="/admin/checkins">Terug naar overzicht</Link>
          </div>
        </div>
      </section>
    );
  }

  const { reservation, trailer, customer, checkoutInspection, existingDamageCases = [] } = data;

  return (
    <section className="page checkout-mobile admin-checkout-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Check-in uitvoeren</h1>
          <p className="page-subtitle">Terugnamecontrole voor reservatie #{reservation.id}</p>
        </div>
        <Link className="button secondary" to="/admin/checkins">Terug</Link>
      </header>

      <section className="panel panel-pad">
        <h2 className="detail-title">Reservatie</h2>
        <div className="detail-grid">
          <div className="detail-item"><div className="detail-label">Periode</div><div className="detail-value">{formatDate(reservation.startDate)} tot {formatDate(reservation.endDate)}</div></div>
          <div className="detail-item"><div className="detail-label">Klant</div><div className="detail-value">{`${customer?.firstName || reservation.firstName || ""} ${customer?.lastName || reservation.name || ""}`.trim() || "-"}</div></div>
          <div className="detail-item"><div className="detail-label">Trailer</div><div className="detail-value">{trailer?.name || "-"}</div></div>
          <div className="detail-item"><div className="detail-label">Status</div><div className="detail-value">{reservation.status}</div></div>
        </div>
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Checkout info</h2>
        {!checkoutInspection ? <div className="empty-state">Geen checkout-inspectie gevonden.</div> : (
          <div className="photo-grid">
            {checkoutInspection.photos?.map((photo) => (
              <button key={photo.id} type="button" className="checkout-admin-photo" onClick={() => setPreviewPhoto(photo)}>
                <img className="photo-thumb" src={`${imageBase}${photo.imageUrl}`} alt={photo.label} />
                <span>{photo.label || photo.type}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Bekende schadegevallen</h2>
        {existingDamageCases.length === 0 && <div className="empty-state">Geen schadegevallen gekend.</div>}
        {existingDamageCases.map((damageCase) => (
          <div key={damageCase.id} className="checkout-damage">
            <span className="badge danger">{damageCase.status}</span>
            <p className="page-subtitle">{damageCase.description || "Geen omschrijving"}</p>
          </div>
        ))}
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Trailerfoto's</h2>
        <div className="checkout-checklist">
          {trailerChecklist.map((item) => {
            const photo = photos[item.type];
            const inputId = `checkin-photo-${item.type}`;
            return (
              <div key={item.type} className={`checkout-photo-item${photo ? " complete" : ""}`}>
                <div className="checkout-photo-copy">
                  <span className="detail-label">Trailerpositie</span>
                  <strong>{item.label}</strong>
                  <span className={`checkout-photo-status${photo ? " complete" : ""}`}>{photo ? "Foto toegevoegd" : "Nog niet toegevoegd"}</span>
                </div>
                {photo ? <img className="checkout-photo-preview" src={`${imageBase}${photo.imageUrl}`} alt={item.label} /> : <div className="checkout-photo-placeholder">Geen foto</div>}
                <input id={inputId} className="checkout-file-input" type="file" accept="image/jpeg,image/png" capture="environment" onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    const imageUrl = await uploadPhoto(file);
                    setPhotos({ ...photos, [item.type]: { type: item.type, label: item.label, imageUrl } });
                  }
                  event.target.value = "";
                }} />
                <label className="button checkout-camera-button" htmlFor={inputId}>{photo ? "Foto vervangen" : "Foto nemen"}</label>
              </div>
            );
          })}
        </div>
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Opties/accessoires</h2>
        {optionChecklist.length === 0 && <div className="empty-state">Geen opties of accessoires opgegeven.</div>}
        <div className="checkout-checklist">
          {optionChecklist.map((item) => {
            const check = optionChecks[item.optionName] || { optionName: item.optionName, status: "OK", notes: "", photoUrl: "" };
            const inputId = `checkin-option-${item.optionName}`;
            return (
              <div key={item.optionName} className={`checkout-photo-item${check.photoUrl ? " complete" : ""}`}>
                <div className="checkout-photo-copy">
                  <span className="detail-label">Optie/accessoire</span>
                  <strong>{item.optionName}</strong>
                </div>
                <select className="select" value={check.status} onChange={(event) => setOptionChecks({ ...optionChecks, [item.optionName]: { ...check, status: event.target.value } })}>
                  <option value="OK">OK</option>
                  <option value="DAMAGED">Beschadigd</option>
                  <option value="MISSING">Ontbreekt</option>
                </select>
                <textarea className="textarea" placeholder="Opmerking" value={check.notes} onChange={(event) => setOptionChecks({ ...optionChecks, [item.optionName]: { ...check, notes: event.target.value } })} />
                {check.photoUrl ? <img className="checkout-photo-preview" src={`${imageBase}${check.photoUrl}`} alt={item.optionName} /> : <div className="checkout-photo-placeholder">Geen foto</div>}
                <input id={inputId} className="checkout-file-input" type="file" accept="image/jpeg,image/png" capture="environment" onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    const photoUrl = await uploadPhoto(file);
                    setOptionChecks({ ...optionChecks, [item.optionName]: { ...check, photoUrl } });
                  }
                  event.target.value = "";
                }} />
                <label className="button checkout-camera-button" htmlFor={inputId}>{check.photoUrl ? "Foto vervangen" : "Foto nemen"}</label>
              </div>
            );
          })}
        </div>
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Schade / issues</h2>
        <label className="check-field">
          <input type="checkbox" checked={hasDamage} onChange={(event) => setHasDamage(event.target.checked)} />
          <span>Nieuwe schade vastgesteld</span>
        </label>
        {hasDamage && (
          <>
            <label className="field" style={{ marginTop: 12 }}>
              <span className="field-label">Schadeomschrijving</span>
              <textarea className="textarea" value={damageDescription} onChange={(event) => setDamageDescription(event.target.value)} />
              {damageDescriptionMissing && <span className="field-error">Beschrijf de vastgestelde schade.</span>}
            </label>
            <div className={`document-upload-card${damagePhotos.length ? " complete" : ""}`} style={{ marginTop: 12 }}>
              <div className="document-upload-copy">
                <span className="field-label">Schadefoto's</span>
                <strong>{damagePhotos.length ? "Schadefoto toegevoegd" : "Geen schadefoto toegevoegd"}</strong>
                <span className={`checkout-photo-status${damagePhotos.length ? " complete" : ""}`}>
                  {damagePhotos.length ? `${damagePhotos.length} foto${damagePhotos.length === 1 ? "" : "'s"}` : "Verplicht bij schade"}
                </span>
              </div>
              <div className="document-upload-preview-grid">
                {damagePhotos.length ? damagePhotos.map((photo, index) => (
                  <div key={photo.imageUrl} className="document-upload-preview">
                    <img src={`${imageBase}${photo.imageUrl}`} alt={photo.label} />
                    <button
                      type="button"
                      className="document-remove-button"
                      onClick={() => setDamagePhotos(damagePhotos.filter((_, photoIndex) => photoIndex !== index))}
                    >
                      Verwijderen
                    </button>
                  </div>
                )) : (
                  <div className="checkout-photo-placeholder">Geen schadefoto</div>
                )}
              </div>
              <input
                id="checkin-damage-photo"
                className="checkout-file-input"
                type="file"
                accept="image/jpeg,image/png"
                capture="environment"
                multiple
                onChange={async (event) => {
                  const files = Array.from(event.target.files || []);
                  const uploadedPhotos = [];

                  for (const file of files) {
                    if (!file.type.startsWith("image/")) {
                      toast.error("Gebruik enkel afbeeldingen of foto's.");
                      continue;
                    }

                    const imageUrl = await uploadPhoto(file);
                    uploadedPhotos.push({
                      type: "damage",
                      label: "Nieuwe schade",
                      imageUrl
                    });
                  }

                  if (uploadedPhotos.length) {
                    setDamagePhotos([...damagePhotos, ...uploadedPhotos]);
                  }
                  event.target.value = "";
                }}
              />
              <label className="button checkout-camera-button" htmlFor="checkin-damage-photo">
                Schadefoto nemen
              </label>
              <p className="row-meta checkout-photo-hint">Op gsm opent dit de camera waar ondersteund. Galerij upload blijft mogelijk.</p>
            </div>
            {damagePhotoMissing && <p className="error-text">Voeg minstens één schadefoto toe.</p>}
          </>
        )}
      </section>

      <section className="panel panel-pad">
        <label className="field">
          <span className="field-label">Algemene opmerkingen</span>
          <textarea className="textarea" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
      </section>

      <section className="panel panel-pad">
        <SignaturePad
          label="Digitale handtekening"
          confirmationText="Ik bevestig dat ik de aanhangwagen in bovenstaande staat ontvang."
          value={signatureDataUrl}
          onChange={(nextSignature) => {
            setSignatureDataUrl(nextSignature);
            if (nextSignature) setSignatureError("");
          }}
          error={signatureError}
        />
      </section>

      <div className="checkout-submit-bar">
        <div>
          <strong>{canSubmit ? "Alles klaar" : "Controle ontbreekt"}</strong>
          <span>{uploadMutation.isPending ? "Foto uploaden..." : "Vul alle verplichte controles in."}</span>
        </div>
        <button className="button checkout-submit-button" disabled={!requiredChecksComplete || uploadMutation.isPending || confirmMutation.isPending} onClick={submitCheckin}>
          {confirmMutation.isPending ? "Bevestigen..." : "Check-in bevestigen"}
        </button>
      </div>
      {previewPhoto && (
        <button className="image-modal" onClick={() => setPreviewPhoto(null)}>
          <span className="modal-close" aria-hidden="true">Sluiten</span>
          <figure className="checkout-photo-modal" onClick={(event) => event.stopPropagation()}>
            <img src={`${imageBase}${previewPhoto.imageUrl}`} alt={previewPhoto.label || previewPhoto.type} />
            <figcaption>{previewPhoto.label || previewPhoto.type}</figcaption>
          </figure>
        </button>
      )}
    </section>
  );
}
